import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { getInvoiceDetailsAction, downloadInvoiceFileAction } from './API/Action';
import { CustomButton } from '../../v2/MUI_custom_component';
import { DownloadOutlined } from '../../components/icons';
import dayjs from 'dayjs';

const InvoiceDetails = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceFile, setInvoiceFile] = useState([]);
  const [testCertificateFile, setTestCertificateFile] = useState([]);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'));


  const {
    selectedSupplier: selectedManufacturer,
    selectedEntity: selectedCompanyEntity,
  } = useSelector((state) => state.entityReducer);
  const fetchInvoiceDetails = useCallback(async () => {
    let invoiceIdFromUrl = params.invoice_id || params.id;
    if (!invoiceIdFromUrl) {
      const pathParts = location.pathname.split('/');
      invoiceIdFromUrl = pathParts[pathParts.length - 1];
    }

    setLoading(true);
    const config = new URLSearchParams();
    config.append('invoice_id', invoiceIdFromUrl);

    const { data, error, message } = await getInvoiceDetailsAction(config);

    if (!error) {
      setInvoiceData(data);

      const invoiceFiles = data.invoice_file_details?.filter(
        file => file.file_type === 'invoice'
      ) || [];
      setInvoiceFile(invoiceFiles);

      const testCertFiles = data.invoice_file_details?.filter(
        file => file.file_type === 'test_certificate'
      ) || [];

      setTestCertificateFile(testCertFiles);
      
    } else {
      toast.error(message || 'Failed to fetch invoice details');
    }
    setLoading(false);
  }, [params.invoice_id, params.id, location.pathname]);
  useEffect(() => {
    if (selectedManufacturer) {
      fetchInvoiceDetails();
    }
  }, [fetchInvoiceDetails, selectedManufacturer, selectedCompanyEntity]);
  

  if (loading && !invoiceData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!invoiceData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Typography variant='h3'>No Invoice data available</Typography>
      </Box>
    );
  }
  const {
    invoice_details,
    total_info,
    bill_info,
    delivery_vehicle_details,
    shipping_info,
    invoiced_products_details,
  } = invoiceData;

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Invoice Details */}
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={4}
        >
          <Box
            sx={{
              background: '#fff',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography variant='h2'>Invoice Details</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '90px', md: '120px' }, color: '#666' }}
                >
                  Invoice Number
                </Typography>
                <Typography variant='p3'>
                  {invoice_details.invoice_no}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '90px', md: '120px' }, color: '#666' }}
                >
                  Invoice Date
                </Typography>
                <Typography variant='p3'>
                    {dayjs(invoice_details.date_of_invoice).format('DD/MM/YYYY')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '90px', md: '120px' }, color: '#666' }}
                >
                  Order ID
                </Typography>
                <Typography variant='p3'>{invoice_details.order_id}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '90px', md: '120px' }, color: '#666' }}
                >
                  Invoice Qty.
                </Typography>
                <Typography variant='p3'>{total_info.quantity}</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        {!isMobile && (
          <Grid
            item
            xs={12}
            md={4}
          >
            <Box
              sx={{
                background: '#fff',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant='h2'>Billing Details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {bill_info?.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop:
                        index === bill_info.length - 1
                          ? '1px solid #D9D9D9'
                          : 'none',
                      pt: index === bill_info.length - 1 ? 1 : 0,
                    }}
                  >
                    <Typography variant='labelM'>
                      {item.display_name}
                    </Typography>
                    <Typography
                      variant='p3'
                      sx={{ fontWeight: index === bill_info.length - 1 && 600 }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        )}
        {!isMobile && (
          <Grid
            item
            xs={12}
            md={4}
          >
            <Box
              sx={{
                background: '#fff',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant='h2'>Logistic Details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='labelM'>Vehicle Driver Name</Typography>
                  <Typography variant='p3'>
                    {delivery_vehicle_details.vehicle_driver_name || '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='labelM'>
                    Vehicle Driver Mobile Number
                  </Typography>
                  <Typography variant='p3'>
                    {delivery_vehicle_details.vehicle_driver_phone_no || '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='labelM'>Vehicle No.</Typography>
                  <Typography variant='p3'>
                    {delivery_vehicle_details.vehicle_number || '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Shipping Info */}
      <Box
        sx={{
          background: '#fff',
          borderRadius: 2,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant='h2'>Shipping Info</Typography>
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '90px', md: '120px' }, color: '#666' }}
                >
                  Firm Name
                </Typography>
                <Typography variant='p3'>
                  {shipping_info?.company_name}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '90px', md: '120px' }, color: '#666' }}
                >
                  Mobile Number
                </Typography>
                <Typography variant='p3'>
                  {shipping_info?.company_phone_number}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '90px', md: '120px' }, color: '#666' }}
                >
                  Address
                </Typography>
                <Typography variant='p3'>
                  {shipping_info?.company_address}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: 2 }}>
              {invoiceFile.length > 0 && (
                <CustomButton
                  variant='outlined'
                  color='primary'
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => {
                    invoiceFile.forEach((file) =>
                      downloadInvoiceFileAction(file.invoice_file_id, file.filename, file.file_type)
                    );
                  }}
                  label='Invoice'
                  leftIcon={DownloadOutlined}
                />
              )}
              {testCertificateFile.length > 0 && (
                <CustomButton
                  variant='outlined'
                  color='primary'
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => {
                    testCertificateFile.forEach((file) =>
                      downloadInvoiceFileAction(file.invoice_file_id, file.filename, file.file_type)
                    );
                  }}
                  label='Test Certificate'
                  leftIcon={DownloadOutlined}
                />
              )}
            </Box>
          </Grid>
        </Grid>

        <TableContainer
          component={Paper}
          variant='outlined'
          sx={{
            border: 'none',
            boxShadow: 'none',
            borderRadius: '16px',
            overflowX: 'scroll',
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#E5E5E5',
                  '& .MuiTableCell-root': {
                    borderRight: 'none',
                    borderBottom: '1px solid #8C8C8C',
                  },
                }}
              >
                <TableCell>
                  <Typography
                    variant='labelM'
                    sx={{ minWidth: { xs: '120px', md: '200px' } }}
                  >
                    Product Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant='labelM'
                    sx={{ minWidth: { xs: '120px', md: '200px' } }}
                  >
                    Invoiced Qty.
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant='labelM'
                    sx={{ minWidth: { xs: '120px', md: '200px' } }}
                  >
                    Price/Unit INR
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant='labelM'
                    sx={{ minWidth: { xs: '120px', md: '200px' } }}
                  >
                    Discount UOM wise
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant='labelM'
                    sx={{ minWidth: { xs: '120px', md: '200px' } }}
                  >
                    Total Value INR
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant='labelM'
                    sx={{ minWidth: { xs: '120px', md: '200px' } }}
                  >
                    Taxable Value INR
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiced_products_details?.map((product, productIndex) => (
                <TableRow
                  key={productIndex}
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiTableCell-root': {
                      borderBottom: 'none',
                      borderRight: 'none',
                      padding: { xs: 1, md: 2 },
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant='p3'>{product.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='p3'>
                      {product.quantity.quantity_in_metric_ton}{' '}
                      {product.quantity.unit}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='p3'>
                      ₹ {product.price_per_unit}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='p3'>
                      ₹ {product.sku_wise_discount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='p3'>
                      ₹ {product.net_price_ex_gst}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='p3'>
                      ₹ {product.total_ex_gst}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* billing details */}
      {isMobile && (
        <Box
          sx={{
            background: '#fff',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant='h2'>Billing Details</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {bill_info?.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop:
                    index === bill_info.length - 1
                      ? '1px solid #D9D9D9'
                      : 'none',
                  pt: index === bill_info.length - 1 ? 1 : 0,
                }}
              >
                <Typography variant='labelM'>{item.display_name}</Typography>
                <Typography
                  variant='p3'
                  sx={{ fontWeight: index === bill_info.length - 1 && 600 }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {/* Logictic Details */}
      {isMobile && (
        <Box
          sx={{
            background: '#fff',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant='h2'>Logistic Details</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='labelM'>Vehicle Driver Name</Typography>
              <Typography variant='p3'>
                {delivery_vehicle_details.vehicle_driver_name || '-'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='labelM'>
                Vehicle Driver Mobile Number
              </Typography>
              <Typography variant='p3'>
                {delivery_vehicle_details.vehicle_driver_phone_no || '-'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='labelM'>Vehicle No.</Typography>
              <Typography variant='p3'>
                {delivery_vehicle_details.vehicle_number || '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default InvoiceDetails;
